import { isDevelopment } from "../../../common/helpers/env";
import React from "react";
import { captureException, withScope } from "@sentry/react";
import { tryReloadOnChunkLoadError } from "../../utils/chunkLoadError";

import { PublishButton } from "../../../common/styles";
import Tooltip from "../Tooltip";
import Icons from "../../icons";
import Icon from "../../icon";
import { FormattedMessage } from "react-intl";

import Form, { InputTextArea, Submit } from "../Form";
import { ErrorPage, LogoCropper, RefreshWrapper, Wrapper, Success } from "./styles";
import messages from "./messages";

import { IProps, IState } from "./interfaces";
import FormattedHtmlMessage from "../../../common/helpers/FormattedHtmlMessage";

class ErrorBoundry extends React.Component<IProps, IState> {
    state: IState = {
        hasError: false,
        isWriting: false,
        comments: "",
        event_id: null,
        loading: false,
        success: false,
        error: false,
        validationComments: undefined,
    };

    componentDidCatch(error: Error, info: any) {
        if (tryReloadOnChunkLoadError(error)) {
            return;
        }
        if (!isDevelopment) {
            withScope(async scope => {
                scope.setExtra("componentStack", info);
                const event_id = await captureException(error);
                this.setState({ event_id });
            });
        }
        this.setState({ hasError: true });
    }

    submit = () => {
        const { user, post } = this.props;
        const { comments, event_id } = this.state;
        const email = user?.email || "error@musicators.com";
        const name = user?.username || "Anonymous";
        if (!comments) return this.setState({ validationComments: "required" });
        this.setState({ loading: true, error: false });
        post(
            "error-user-feedback",
            {
                email,
                name,
                comments,
                event_id,
            },
            res => {
                if (res?.data?.success) {
                    this.setState({ loading: false, success: true });
                } else {
                    this.setState({ loading: false, error: true });
                }
            },
            () => {
                this.setState({ loading: false, error: true });
            }
        );
    };
    render() {
        const {
            intl: { formatMessage, locale },
        } = this.props;
        const { hasError, isWriting, comments, loading, success, error, validationComments } = this.state;
        if (hasError) {
            return (
                <ErrorPage>
                    <Wrapper>
                        <div>
                            <LogoCropper>
                                <Icons.Logo locale={locale} />
                            </LogoCropper>
                        </div>
                        <h1>
                            <FormattedMessage {...messages.h1} />
                        </h1>
                        <h2>
                            <FormattedMessage {...messages.h2} />
                        </h2>
                        <p>
                            <FormattedHtmlMessage {...messages.p} />
                        </p>
                        <RefreshWrapper>
                            <Tooltip label={formatMessage(messages.reloadTooltipLabel)}>
                                <PublishButton
                                    className="Active"
                                    disabled={loading}
                                    onClick={() => window.location.reload()}
                                >
                                    <Icon i="refresh" />
                                </PublishButton>
                            </Tooltip>
                            {!success && (
                                <Tooltip label={formatMessage(messages.messageTooltipLabel)}>
                                    <PublishButton
                                        className="Active"
                                        disabled={loading}
                                        onClick={() => this.setState({ isWriting: !isWriting })}
                                    >
                                        <Icon i="edit" />
                                    </PublishButton>
                                </Tooltip>
                            )}
                        </RefreshWrapper>
                        {isWriting && !success && (
                            <Form disabled={loading}>
                                <InputTextArea
                                    validation={validationComments || (error ? "unknownError" : undefined)}
                                    label={formatMessage(messages.commentsLabel)}
                                    required
                                    value={comments}
                                    onChange={e =>
                                        this.setState({ comments: e.currentTarget.value, validationComments: undefined })
                                    }
                                />
                                <Submit onClick={this.submit}>
                                    <FormattedMessage {...messages.submit} />
                                </Submit>
                            </Form>
                        )}
                        {success && (
                            <Success>
                                <Icon i="check" />
                                <FormattedMessage {...messages.thankYou} />
                            </Success>
                        )}
                    </Wrapper>
                </ErrorPage>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundry;
